import { useCallback } from "react";

interface EnvVariable {
  key: string;
  value: string;
  isModified?: boolean;
  originalValue?: string;
  isDeleted?: boolean;
}

export const useEnvParser = () => {
  const parseEnv = useCallback((envString: string): EnvVariable[] => {
    const env: Record<string, string> = {};
    const lines = envString.split("\n");

    for (const line of lines) {
      if (!line || line.startsWith("#")) continue;

      // eslint-disable-next-line prefer-const
      let [key, ...valueParts] = line.split("=");
      let value = valueParts.join("=");

      key = key.trim();
      value = value.trim();

      if (value.startsWith('"') && value.endsWith('"'))
        value = value.slice(1, -1);
      if (value.startsWith("'") && value.endsWith("'"))
        value = value.slice(1, -1);

      value = value
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\\\/g, "\\")
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'");

      if (key) {
        env[key] = value;
      }
    }

    return Object.entries(env).map(([key, value]) => ({
      key,
      value,
      isModified: false,
      isDeleted: false,
    }));
  }, []);

  const handlePasteEnv = useCallback(
    (
      pastedText: string,
      onUpdateEnvVars: (
        updater: (prev: EnvVariable[]) => EnvVariable[],
      ) => void,
    ) => {
      if (!pastedText) return;

      const parsedEnvVars = parseEnv(pastedText);

      if (parsedEnvVars.length === 0) return;

      onUpdateEnvVars((prev) => {
        const filteredPrev = prev.filter((env) => env.key || env.value);
        const newEntries = parsedEnvVars.map((env) => ({
          ...env,
          isModified: true,
          isDeleted: false,
        }));
        return [...filteredPrev, ...newEntries];
      });
    },
    [parseEnv],
  );

  return {
    parseEnv,
    handlePasteEnv,
  };
};
